// QA render-only fixtures for the provider report (05_FIXTURE_SPEC.md).
// These are FAKE payloads fed straight into ReportViewComponent via
//   /<lang>/provider-report/preview?fixture=<name>
// No DB, no accounts - just rendering, so QA can eyeball the edge cases the real
// #61 data never exercises (empty states, first scan, sparse history, partial
// cognitive, and the worst-case density). Any field not named in the spec is set
// to a sensible neutral value.

import {
  MedicationEntry,
  ReportSignalEntry,
  ReportSymptomEntry,
  ReportTimelinePoint,
  ReportViewData,
} from '../../services/doctor-report.service';

type SymMap = Record<string, number>;

/** A cognitive signal; pass score=null for a domain that wasn't administered. */
const cog = (key: string, score: number | null): ReportSignalEntry =>
  score === null ? { key, value: '', notMeasured: true } : { key, value: String(score), score };

const med = (name: string, dose: string, frequency: string, consistency: string, reason = '', startDate = ''): MedicationEntry => ({
  name, dose, frequency, frequencyCustom: '', startDate, approximateStart: '', consistency, sideEffects: '', reason,
});

const point = (date: string, week: number, scanNumber: number, symptoms: SymMap): ReportTimelinePoint =>
  ({ date, week, scanNumber, symptoms });

/** topSymptoms (value + delta vs the previous rated scan) - the yellow "rose" card
 *  and the EHR change line read this, not the timeline. */
const tops = (cur: SymMap, prev: SymMap | null): ReportSymptomEntry[] =>
  Object.entries(cur).map(([key, value]) => ({ key, value, delta: prev ? value - (prev[key] ?? 0) : null }));

/** A sense-2.0 record. null value => that metric was inconclusive (not measured). */
const senseRec = (vision: number | null, hearing: number | null, taken_at: string) => ({
  module: 'sense', engine_version: 'sense-2.0', taken_at,
  vision_contrast: { status: vision == null ? 'inconclusive' : 'ok', log_cs: vision, code: 200, level_index: 4, levels_total: 6, presented: 6, warmup_fails: 0 },
  hearing_tone: { status: hearing == null ? 'inconclusive' : 'ok', snr_db: hearing, trials: 6, reversals: 3, reversals_used: 3 },
  context: { ui_lang: 'he' },
});

const base = (p: Partial<ReportViewData>): ReportViewData => ({
  patientName: 'דמו', patientAge: '45', visitNumber: 1, checkDate: '21.06.2026',
  scanCount: 1, symptomScanCount: 1, treatmentStatus: '', medications: [], reportPurpose: '',
  visitFocusTags: [], overallPattern: 'stable', improvements: [], worsened: [], stableAreas: [],
  topSymptoms: [], symptomClusters: [], changesImproved: [], changesIncreased: [],
  symptomTimeline: [], hrtEvents: [], cognitiveSignals: [], physiologicalSignals: [],
  bleedingPatterns: [], bleedingNotes: '', doctorNotes: '', visitGoal: 'תמיכה בשינה ובדחיפות שתן',
  sideEffects: [], sideEffectEvents: [], ...p,
});

const THREE_MEDS = [
  med('מגנזיום', '100', 'daily', 'very_consistently', 'שינה', '2026-01-15'),
  med('פרוגסטרון', '200', 'nightly', 'mostly_consistently', '', '2026-03-02'),
  med('אסטרוגן', '100', 'as_needed', 'sometimes_missed'),
];

// ── 1 · worst_case : 24 symptoms high, stable (no improvement), all 6 cognitive low ──
const WORST: SymMap = {
  energy_dips: 10, sugar_cravings: 10, daytime_sleepiness: 10, unrefreshing_sleep: 10,
  insomnia_staying_asleep: 10, hot_flashes: 10, night_sweats: 10, brain_fog: 10,
  urinary_frequency: 9, anxiety: 9, early_awakening: 9, joint_pain: 9, hand_wrist_discomfort: 9, mood_swings: 9,
  constipation: 8, back_pain: 8, weight_gain: 8, stress_sensitivity: 8, low_mood: 8, irritability: 8,
  insomnia_falling_asleep: 8, dry_skin: 8, hair_thinning: 8, itchy_sensitive_skin: 8,
};
const worst_case = base({
  patientName: 'דמו קשה', visitNumber: 3, scanCount: 3, symptomScanCount: 3,
  medications: THREE_MEDS,
  symptomTimeline: [
    point('2025-12-22', 0, 1, WORST),
    point('2026-06-12', 24, 2, WORST),
    point('2026-06-21', 25, 3, WORST),
  ],
  topSymptoms: tops(WORST, WORST),
  cognitiveSignals: [
    cog('reactionTime', 22),
    cog('workingMemorySpan', 20), cog('wordRetrieval', 28), cog('delayedRecall', 15),
  ],
  sense: { scans: [senseRec(1.20, -1.0, '2025-12-22'), senseRec(1.35, -2.0, '2026-06-12'), senseRec(1.50, -5.0, '2026-06-21')] },
  domainTrends: {
    dates: ['2025-12-22', '2026-06-12', '2026-06-21'],
    cognitive: [
      { key: 'reactionTime', series: [34, 27, 22] },
      { key: 'workingMemorySpan', series: [31, 24, 20] },
      { key: 'wordRetrieval', series: [35, 30, 28] },
      { key: 'delayedRecall', series: [26, null, 15] },
    ],
    facial: [
      { key: 'skinDryness', series: [48, 55, 62] },
      { key: 'fineLines', series: [40, 44, 45] },
    ],
  },
  safetyFlags: ['chest_pressure', 'new_tremor'],
  sideEffectEvents: [
    { date: '2026-06-12', label: 'בחילה בבקרים', medication: 'פרוגסטרון' },
    { date: '2026-06-21', label: 'רגישות בשד', medication: 'אסטרוגן' },
  ],
});

// ── 2 · best_case : all low, 6 cluster symptoms resolved from last visit, cognitive high ──
const BEST_CUR: SymMap = {
  hot_flashes: 2, night_sweats: 2, daytime_sleepiness: 2, mood_swings: 2,
  insomnia_staying_asleep: 1, insomnia_falling_asleep: 1, early_awakening: 1, unrefreshing_sleep: 1,
  joint_pain: 1, stiffness: 1, muscle_aches: 1, weight_gain: 1, sugar_cravings: 1, energy_dips: 1, dry_skin: 1,
  brain_fog: 0, motivation_drop: 0, anxiety: 0, low_mood: 0, irritability: 0, forgetfulness: 0, difficulty_concentrating: 0,
};
const BEST_PREV: SymMap = { ...BEST_CUR, brain_fog: 8, motivation_drop: 7, anxiety: 6, low_mood: 6, irritability: 5, mood_swings: 5 };
const best_case = base({
  patientName: 'דמו קל', visitNumber: 2, scanCount: 2, symptomScanCount: 2,
  symptomTimeline: [
    point('2026-05-03', 0, 1, BEST_PREV),
    point('2026-06-21', 7, 2, BEST_CUR),
  ],
  topSymptoms: tops(BEST_CUR, BEST_PREV),
  cognitiveSignals: [
    cog('reactionTime', 91),
    cog('workingMemorySpan', 87), cog('wordRetrieval', 90), cog('delayedRecall', 95),
  ],
  domainTrends: {
    dates: ['2026-05-03', '2026-06-21'],
    cognitive: [
      { key: 'reactionTime', series: [84, 91] },
      { key: 'workingMemorySpan', series: [80, 87] },
      { key: 'wordRetrieval', series: [86, 90] },
      { key: 'delayedRecall', series: [88, 95] },
    ],
    facial: [{ key: 'skinDryness', series: [70, 74] }],
  },
});

// ── 3 · first_scan : one scan only, no delta anywhere; the 4 short-battery domains ──
const FIRST: SymMap = {
  insomnia_staying_asleep: 9, energy_dips: 8, anxiety: 7, brain_fog: 6, mood_swings: 5,
  joint_pain: 4, hot_flashes: 3, night_sweats: 3, weight_gain: 2, dry_skin: 2, sugar_cravings: 1, constipation: 1,
};
const first_scan = base({
  patientName: 'דמו ראשון', visitNumber: 1, scanCount: 1, symptomScanCount: 1,
  medications: THREE_MEDS,
  symptomTimeline: [point('2026-06-21', 0, 1, FIRST)],
  topSymptoms: tops(FIRST, null),
  cognitiveSignals: [
    cog('reactionTime', 77),
    cog('workingMemorySpan', 42), cog('wordRetrieval', 52), cog('delayedRecall', 100),
  ],
});

// ── 4 · sparse_history : 20 scans, only 3 rated, deliberately uneven dates ──
const SPARSE_A: SymMap = { hot_flashes: 6, night_sweats: 6, insomnia_staying_asleep: 8, brain_fog: 7, anxiety: 6, joint_pain: 5, mood_swings: 5, energy_dips: 6 };
const SPARSE_B: SymMap = { hot_flashes: 4, night_sweats: 5, insomnia_staying_asleep: 7, brain_fog: 4, anxiety: 5, joint_pain: 6, mood_swings: 4, energy_dips: 7 };
const SPARSE_C: SymMap = { hot_flashes: 2, night_sweats: 3, insomnia_staying_asleep: 6, brain_fog: 1, anxiety: 4, joint_pain: 7, mood_swings: 3, energy_dips: 8 };
const sparse_history = base({
  patientName: 'דמו דליל', visitNumber: 18, scanCount: 20, symptomScanCount: 3,
  symptomTimeline: [
    point('2026-01-15', 0, 6, SPARSE_A),
    point('2026-05-03', 15, 13, SPARSE_B),
    point('2026-06-21', 22, 20, SPARSE_C),
  ],
  topSymptoms: tops(SPARSE_C, SPARSE_B),
  cognitiveSignals: [
    cog('reactionTime', 70),
    cog('workingMemorySpan', 60), cog('wordRetrieval', 58), cog('delayedRecall', 80),
  ],
});

// ── 5 · partial_cognitive : the 4 short-battery domains, unchanged across scans ──
// THINK = mean of the 4 composite domains (77,52,42,100)=68 both scans -> no drop.
const PC_SYM: SymMap = { insomnia_staying_asleep: 7, brain_fog: 5, anxiety: 5, mood_swings: 4, joint_pain: 4, energy_dips: 6 };
const PC_PREV: SymMap = { insomnia_staying_asleep: 7, brain_fog: 5, anxiety: 5, mood_swings: 4, joint_pain: 4, energy_dips: 6 };
const partial_cognitive = base({
  patientName: 'דמו קוגניטיבי', visitNumber: 2, scanCount: 2, symptomScanCount: 2,
  symptomTimeline: [
    point('2026-05-03', 0, 1, PC_PREV),
    point('2026-06-21', 7, 2, PC_SYM),
  ],
  topSymptoms: tops(PC_SYM, PC_PREV),
  cognitiveSignals: [
    cog('reactionTime', 77),
    cog('workingMemorySpan', 42), cog('wordRetrieval', 52), cog('delayedRecall', 100),
  ],
});

// ── 6 · unrated_zeros : round-6 base (Dana I, first scan, e742c8b1). 7 symptoms
// rated >0; the rest were SHOWN and rated 0 ("I don't have it") - valid answers, not
// missing data. Exercises fix 47 (the cluster card must collapse the ~16 zero rows,
// not print a whole page of them) + fix 50 (context line names each confounder) +
// fix 54 (sensations "not reported"). 5 of the 7 rated symptoms fall outside every
// current cluster (met / uro / sens / hand) - that gap is fix 48 (blocked on the
// catalog cluster field), so here they surface only in the appendix.
const UNRATED_ZEROS: SymMap = {
  sugar_cravings: 9, daytime_sleepiness: 9, urinary_urgency: 7, hand_wrist_discomfort: 7,
  dry_eyes_gritty_feeling: 7, early_awakening: 7, motivation_drop: 7,
  hot_flashes: 0, night_sweats: 0,
  insomnia_staying_asleep: 0, insomnia_falling_asleep: 0, unrefreshing_sleep: 0,
  joint_pain: 0, stiffness: 0, muscle_aches: 0,
  brain_fog: 0, difficulty_concentrating: 0, forgetfulness: 0, slow_recall: 0,
  mood_swings: 0, low_mood: 0, anxiety: 0, irritability: 0,
};
const unrated_zeros = base({
  patientName: 'דנה I', patientAge: '45', visitNumber: 1, checkDate: '07.06.2026',
  scanCount: 1, symptomScanCount: 1, visitGoal: 'גלי חום',
  medications: THREE_MEDS,
  symptomTimeline: [point('2026-06-07', 0, 1, UNRATED_ZEROS)],
  topSymptoms: tops(UNRATED_ZEROS, null),
  cognitiveSignals: [
    cog('reactionTime', 74),
    cog('workingMemorySpan', 66), cog('wordRetrieval', 58), cog('delayedRecall', 80),
  ],
});

// ── 7 · sense_first_scan : SENSE block on a NEW user - the class of bugs the
// prior QA rounds (all on a full fixture) never hit. Vision measured, hearing not
// completed; note column, no comparison, "not measured" explicit. alsoReported
// links the reported sensory symptoms. ──
const SENSE_FIRST_SYM: SymMap = { vision_fluctuation: 6, tinnitus_ringing: 4, insomnia_staying_asleep: 7, energy_dips: 6 };
const sense_first_scan = base({
  patientName: 'דמו חושים ראשון', visitNumber: 1, scanCount: 1, symptomScanCount: 1,
  symptomTimeline: [point('2026-08-17', 0, 1, SENSE_FIRST_SYM)],
  topSymptoms: tops(SENSE_FIRST_SYM, null),
  cognitiveSignals: [cog('reactionTime', 70), cog('workingMemorySpan', 60), cog('wordRetrieval', 58), cog('delayedRecall', 80)],
  sense: { scans: [senseRec(1.76, null, '2026-08-17')] },
});

// ── 8 · sense_followup : contrast STABLE (shown, not dropped), tone IMPROVED 5 dB.
// Matches the handoff 04 example block. alsoReported cross-links noise/light. ──
const SENSE_FU_SYM: SymMap = { vision_fluctuation: 6, noise_sensitivity: 5, light_sensitivity: 3, insomnia_staying_asleep: 6 };
const sense_followup = base({
  patientName: 'דמו חושים מעקב', visitNumber: 3, scanCount: 3, symptomScanCount: 3,
  symptomTimeline: [point('2026-02-27', 0, 1, SENSE_FU_SYM), point('2026-06-12', 15, 2, SENSE_FU_SYM), point('2026-08-17', 24, 3, SENSE_FU_SYM)],
  topSymptoms: tops(SENSE_FU_SYM, SENSE_FU_SYM),
  cognitiveSignals: [cog('reactionTime', 70), cog('workingMemorySpan', 60), cog('wordRetrieval', 58), cog('delayedRecall', 80)],
  sense: { scans: [senseRec(1.35, -1.0, '2026-02-27'), senseRec(1.76, -3.5, '2026-06-12'), senseRec(1.76, -8.5, '2026-08-17')] },
});

// ── round 8 · cluster_tracking : a busy follow-up that exercises the fix-62/63/66
//    branches the other fixtures don't. 17 of 20 clusters carry a symptom (so only
//    3 stay fully-zero -> the "<=4 named" branch of fix 66), heatCold is MIXED
//    (hot_flashes up + night_sweats down -> "מעורב · 1 ירד, 1 עלה"), and
//    skinNerveSensations drops to 0 (prev>0, cur=0 -> shown as a drop, not hidden). ──
const CT_PREV: SymMap = {
  hot_flashes: 4, night_sweats: 7, insomnia_falling_asleep: 8, joint_pain: 5,
  tingling_or_numbness: 3, grip_strength_loss: 4, energy_dips: 6, bloating: 5,
  heart_palpitations: 4, sugar_cravings: 7, anxiety: 6, mood_swings: 5,
  motivation_drop: 5, brain_fog: 7, hair_thinning: 4, allergy_increase: 3,
  dry_eyes_gritty_feeling: 6, vaginal_dryness: 8, urinary_frequency: 9,
};
const CT_CUR: SymMap = {
  hot_flashes: 6, night_sweats: 5, insomnia_falling_asleep: 6, joint_pain: 5,
  tingling_or_numbness: 0, grip_strength_loss: 6, energy_dips: 8, bloating: 3,
  heart_palpitations: 4, sugar_cravings: 7, anxiety: 2, mood_swings: 1,
  motivation_drop: 5, brain_fog: 3, hair_thinning: 4, allergy_increase: 5,
  dry_eyes_gritty_feeling: 6, vaginal_dryness: 8, urinary_frequency: 9, dry_skin: 4,
};
const cluster_tracking = base({
  patientName: 'דמו מעקב', visitNumber: 2, scanCount: 2, symptomScanCount: 2,
  overallPattern: 'mixed',
  symptomTimeline: [
    point('2026-05-03', 0, 1, CT_PREV),
    point('2026-06-21', 7, 2, CT_CUR),
  ],
  topSymptoms: tops(CT_CUR, CT_PREV),
  medications: THREE_MEDS,
  cognitiveSignals: [cog('reactionTime', 72), cog('wordRetrieval', 58), cog('workingMemorySpan', 61), cog('delayedRecall', 80)],
  // one facial signal, so the preview also exercises the third pairing source
  physiologicalSignals: [{ key: 'skinDryness', value: '62', delta: '-8' }],
});


// ── 10 · treatment_window : three mappings with a medication started BETWEEN the
//    first and the second, so the digest's comparison switch has something real to
//    say: 'since her previous mapping' and 'since this treatment period began'
//    land on different baselines and therefore different numbers. ──
const TW_BASE: SymMap = {
  hot_flashes: 9, night_sweats: 8, insomnia_falling_asleep: 7, brain_fog: 6,
  joint_pain: 5, energy_dips: 7, anxiety: 4, urinary_frequency: 3,
};
const TW_MID: SymMap = { ...TW_BASE, hot_flashes: 5, night_sweats: 5, brain_fog: 5, anxiety: 6 };
const TW_CUR: SymMap = { ...TW_MID, hot_flashes: 3, night_sweats: 4, brain_fog: 4, anxiety: 7, urinary_frequency: 6 };
const treatment_window = base({
  patientName: 'דמו תקופת טיפול', visitNumber: 3, scanCount: 3, symptomScanCount: 3,
  overallPattern: 'mixed',
  symptomTimeline: [
    point('2026-02-01', 0, 1, TW_BASE),
    point('2026-04-05', 9, 2, TW_MID),
    point('2026-06-21', 20, 3, TW_CUR),
  ],
  topSymptoms: tops(TW_CUR, TW_MID),
  medications: [
    med('אסטרדיול', '50', 'daily', 'very_consistently', 'גלי חום', '2026-03-01'),
  ],
  cognitiveSignals: [cog('reactionTime', 68), cog('wordRetrieval', 61), cog('workingMemorySpan', 59), cog('delayedRecall', 74)],
  // Her background and her everyday pattern, so the preview actually exercises the
  // history table. No fixture carried these before, which meant the only way to see
  // those rows was to open a real scan.
  intake: {
    periodLast12: 'yes',
    lastPeriod: 'm1_3',
    contraception: 'no',
    contraceptionMethods: [],
    noBleedReasons: [],
    surgery: null,
    symptoms: [],
    symptomFollowUps: {},
    other: '',
  },
  lifestyle: {
    alcohol: 'weekly_1_2',
    caffeine: 'c2',
    exerciseTypes: ['walking'],
    exerciseOther: '',
    exerciseWeekly: 'w3',
    exerciseChange: 'same',
    exerciseNote: '',
    sleepHours: 'h5_6',
    sleepWaking: 'most_nights',
    sleepChange: 'worse',
    sleepNote: '',
    stress: 'moderate_high',
    smoking: 'never',
    smokingQuitYear: '',
    mealsHome: 'most',
    hotMeals: 'daily',
  },
});

// ── 11 · long_history : Danna's own shape, 61 mappings with one measured ──
// She has scanned for months, and only the latest mapping ran the full battery.
// It is the case that filled the card with sixty "not measured" in a row.
const LONG_SYM: SymMap = { hot_flashes: 5, night_sweats: 4, brain_fog: 6, energy_dips: 6, anxiety: 3 };
const LONG_DATES: string[] = Array.from({ length: 61 }, (_, i) => {
  const day = new Date(Date.UTC(2025, 3, 10 + i * 9));
  return day.toISOString().slice(0, 10);
});
/** null everywhere except the last mapping, which is the only measured one. */
const lastOnly = (value: number): Array<number | null> =>
  LONG_DATES.map((_, i) => (i === LONG_DATES.length - 1 ? value : null));
/** Measured in the last `n` mappings, drifting a little, empty before them. */
const lastN = (value: number, n: number): Array<number | null> =>
  LONG_DATES.map((_, i) =>
    i >= LONG_DATES.length - n ? value - (LONG_DATES.length - 1 - i) : null,
  );
const long_history = base({
  patientName: 'דמו היסטוריה ארוכה', visitNumber: 61, scanCount: 61, symptomScanCount: 2,
  checkDate: '17.09.2026',
  symptomTimeline: [
    point(LONG_DATES[LONG_DATES.length - 2], 0, 60, LONG_SYM),
    point(LONG_DATES[LONG_DATES.length - 1], 9, 61, LONG_SYM),
  ],
  topSymptoms: tops(LONG_SYM, LONG_SYM),
  cognitiveSignals: [
    cog('reactionTime', 68), cog('workingMemorySpan', 53),
    cog('wordRetrieval', 52), cog('delayedRecall', 100),
  ],
  domainTrends: {
    dates: LONG_DATES,
    cognitive: [
      { key: 'reactionTime', series: lastOnly(68) },
      { key: 'workingMemorySpan', series: lastOnly(53) },
      { key: 'wordRetrieval', series: lastOnly(52) },
      { key: 'delayedRecall', series: lastN(100, 20) },
    ],
    facial: [
      { key: 'skinDryness', series: lastOnly(73) },
      { key: 'fineLines', series: lastOnly(77) },
    ],
  },
});

export const REPORT_FIXTURES: Record<string, ReportViewData> = {
  worst_case, best_case, first_scan, sparse_history, partial_cognitive, unrated_zeros,
  sense_first_scan, sense_followup, cluster_tracking, treatment_window, long_history,
};
